import {Trans} from '@lingui/macro';
import {compact} from 'lodash';
import React, {ReactNode} from 'react';

import {makeEnumRuntimeType} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {SettingsPathId} from '../menu/settingsHelpers';

export enum SettingsHomeDestinationsEnum {
  // Private actions
  ADD_PRIVATE_INBOX = 'ADD_PRIVATE_INBOX',
  EDIT_PRIVATE_INBOX = 'EDIT_PRIVATE_INBOX',
  REVIEW_PRIVATE_INBOXES = 'REVIEW_PRIVATE_INBOXES',
  ADD_SIGNATURE = 'ADD_SIGNATURE',
  EDIT_SIGNATURE = 'EDIT_SIGNATURE',
  REVIEW_SIGNATURES = 'REVIEW_SIGNATURES',
  ADD_RESPONDER = 'ADD_RESPONDER',
  EDIT_RESPONDER = 'EDIT_RESPONDER',
  REVIEW_RESPONDERS = 'REVIEW_RESPONDERS',
  ADD_ANSWER = 'ADD_ANSWER',
  EDIT_ANSWER = 'EDIT_ANSWER',
  REVIEW_ANSWERS = 'REVIEW_ANSWERS',
  EDIT_PREFERENCES = 'EDIT_PREFERENCES',

  // Team actions
  ADD_TEAMMATES = 'ADD_TEAMMATES',
  REVIEW_TEAM_INBOXES = 'REVIEW_TEAM_INBOXES',
  ADD_TEAM_INBOX = 'ADD_TEAM_INBOX',
  REVIEW_TEAMMATES = 'REVIEW_TEAMMATES',
  ADD_RULE = 'ADD_RULE',
  REVIEW_RULES = 'REVIEW_RULES',
}

export enum ProfileTogglesEnum {
  OUT_OF_OFFICE = 'OUT_OF_OFFICE',
  MUTE = 'MUTE',
  DO_NOT_DISTURB = 'DO_NOT_DISTURB',
}

export type ProfileTogglesState = {[k in ProfileTogglesEnum]: boolean};

export interface SettingsHomeShortcutDefinition {
  icon: IconName;
  title: ReactNode;
  description: ReactNode;
  primary: {
    label: ReactNode;
    destination: SettingsHomeDestinationsEnum;
  };
  secondary?: {
    label: ReactNode;
    destination: SettingsHomeDestinationsEnum;
  };
}

export function getAdminHomeShortcuts(hasRecentTeammates: boolean) {
  return compact([
    !hasRecentTeammates && {
      icon: 'group' as const,
      title: <Trans id="manage-teammates">Manage teammates</Trans>,
      description: (
        <Trans id="add-people-to-your-team-and-control-what-they-can-access">
          Add people to your team and control what they can access.
        </Trans>
      ),
      primary: {
        label: <Trans id="add-teammates">Add teammates</Trans>,
        destination: SettingsHomeDestinationsEnum.ADD_TEAMMATES,
      },
      secondary: {
        label: <Trans id="review-all">Review all</Trans>,
        destination: SettingsHomeDestinationsEnum.REVIEW_TEAMMATES,
      },
    },
    {
      icon: 'inboxFilled' as const,
      title: <Trans id="manage-inboxes">Manage inboxes</Trans>,
      description: (
        <Trans id="create-shared-inboxes-for-teammates-to-work-together">
          Create shared inboxes for teammates to work together.
        </Trans>
      ),
      primary: {
        label: <Trans id="add-a-team-inbox">Add a team inbox</Trans>,
        destination: SettingsHomeDestinationsEnum.ADD_TEAM_INBOX,
      },
      secondary: {
        label: <Trans id="review-all">Review all</Trans>,
        destination: SettingsHomeDestinationsEnum.REVIEW_TEAM_INBOXES,
      },
    },
    {
      icon: 'gearFilled' as const,
      title: <Trans id="create-rules">Create rules</Trans>,
      description: "Create rules for where messages go and who they're assigned to.",
      primary: {
        label: <Trans id="add-a-team-rule">Add a team rule</Trans>,
        destination: SettingsHomeDestinationsEnum.ADD_RULE,
      },
      secondary: {
        label: <Trans id="review-all">Review all</Trans>,
        destination: SettingsHomeDestinationsEnum.REVIEW_RULES,
      },
    },
  ]);
}

export function getUserHomeShortcuts(
  inboxesCount: number,
  answersCount: number,
  respondersCount: number,
  signaturesCount: number,
): readonly SettingsHomeShortcutDefinition[] {
  return [
    makeInboxShortcut(inboxesCount),
    makeAnswersShortcut(answersCount),
    makeResponderShortcut(respondersCount),
    makeSignatureShortcut(signaturesCount),
  ];
}

function makeAnswersShortcut(answersCount: number): SettingsHomeShortcutDefinition {
  if (answersCount === 0) {
    return {
      icon: 'messageTemplateFilled',
      title: <Trans id="manage-message-templates">Manage message templates</Trans>,
      description: (
        <Trans id="save-templates-to-common-questions-and-respond-faster-to-messages">
          Save templates to common questions and respond faster to messages.
        </Trans>
      ),
      primary: {
        label: <Trans id="add-a-template">Add a template</Trans>,
        destination: SettingsHomeDestinationsEnum.ADD_ANSWER,
      },
    };
  }

  return {
    icon: 'messageTemplateFilled',
    title: <Trans id="manage-message-templates">Manage message templates</Trans>,
    description: (
      <Trans id="save-templates-to-common-questions-and-respond-faster-to-messages">
        Save templates to common questions and respond faster to messages.
      </Trans>
    ),
    primary: {
      label: <Trans id="add-a-template">Add a template</Trans>,
      destination: SettingsHomeDestinationsEnum.ADD_ANSWER,
    },
    secondary: {
      label: <Trans id="review-all">Review all</Trans>,
      destination: SettingsHomeDestinationsEnum.REVIEW_ANSWERS,
    },
  };
}

function makeInboxShortcut(inboxesCount: number): SettingsHomeShortcutDefinition {
  if (inboxesCount === 0) {
    return {
      icon: 'inboxFilled',
      title: <Trans id="manage-inboxes">Manage inboxes</Trans>,
      description: (
        <Trans id="add-your-work-email-as-a-personal-inbox-to-manage-in-front">
          Add your work email as a personal inbox to manage in Front.
        </Trans>
      ),
      primary: {
        label: <Trans id="add-an-inbox">Add an inbox</Trans>,
        destination: SettingsHomeDestinationsEnum.ADD_PRIVATE_INBOX,
      },
    };
  }

  if (inboxesCount === 1) {
    return {
      icon: 'inboxFilled',
      title: <Trans id="manage-inboxes">Manage inboxes</Trans>,
      description: (
        <Trans id="add-your-work-email-as-a-personal-inbox-to-manage-in-front">
          Add your work email as a personal inbox to manage in Front.
        </Trans>
      ),
      primary: {
        label: <Trans id="edit-inbox">Edit inbox</Trans>,
        destination: SettingsHomeDestinationsEnum.EDIT_PRIVATE_INBOX,
      },
      secondary: {
        label: <Trans id="add-inbox">Add inbox</Trans>,
        destination: SettingsHomeDestinationsEnum.ADD_PRIVATE_INBOX,
      },
    };
  }

  return {
    icon: 'inboxFilled',
    title: <Trans id="manage-inboxes">Manage inboxes</Trans>,
    description: (
      <Trans id="add-your-work-email-as-a-personal-inbox-to-manage-in-front">
        Add your work email as a personal inbox to manage in Front.
      </Trans>
    ),
    primary: {
      label: <Trans id="add-an-inbox">Add an inbox</Trans>,
      destination: SettingsHomeDestinationsEnum.ADD_PRIVATE_INBOX,
    },
    secondary: {
      label: <Trans id="review-all">Review all</Trans>,
      destination: SettingsHomeDestinationsEnum.REVIEW_PRIVATE_INBOXES,
    },
  };
}

function makeResponderShortcut(respondersCount: number): SettingsHomeShortcutDefinition {
  if (respondersCount === 0) {
    return {
      icon: 'palmTree',
      title: <Trans id="set-a-vacation-responder">Set a vacation responder</Trans>,
      description: (
        <Trans id="save-auto-replies-and-set-timing-so-you-can-vacation-worry-free">
          Save auto-replies and set timing so you can vacation worry-free.
        </Trans>
      ),
      primary: {
        label: <Trans id="add-a-responder">Add a responder</Trans>,
        destination: SettingsHomeDestinationsEnum.ADD_RESPONDER,
      },
    };
  }

  if (respondersCount === 1) {
    return {
      icon: 'palmTree',
      title: <Trans id="set-a-vacation-responder">Set a vacation responder</Trans>,
      description: (
        <Trans id="save-auto-replies-and-set-timing-so-you-can-vacation-worry-free">
          Save auto-replies and set timing so you can vacation worry-free.
        </Trans>
      ),
      primary: {
        label: <Trans id="edit-responder">Edit responder</Trans>,
        destination: SettingsHomeDestinationsEnum.EDIT_RESPONDER,
      },
      secondary: {
        label: <Trans id="add-responder">Add responder</Trans>,
        destination: SettingsHomeDestinationsEnum.ADD_RESPONDER,
      },
    };
  }

  return {
    icon: 'palmTree',
    title: <Trans id="set-a-vacation-responder">Set a vacation responder</Trans>,
    description: (
      <Trans id="save-auto-replies-and-set-timing-so-you-can-vacation-worry-free">
        Save auto-replies and set timing so you can vacation worry-free.
      </Trans>
    ),
    primary: {
      label: <Trans id="add-a-responder">Add a responder</Trans>,
      destination: SettingsHomeDestinationsEnum.ADD_RESPONDER,
    },
    secondary: {
      label: <Trans id="review-all">Review all</Trans>,
      destination: SettingsHomeDestinationsEnum.REVIEW_RESPONDERS,
    },
  };
}

function makeSignatureShortcut(signaturesCount: number): SettingsHomeShortcutDefinition {
  if (signaturesCount === 0) {
    return {
      icon: 'feather',
      title: <Trans id="manage-signatures">Manage signatures</Trans>,
      description: (
        <Trans id="create-custom-signatures-for-replying-from-different-inboxes">
          Create custom signatures for replying from different inboxes.
        </Trans>
      ),
      primary: {
        label: <Trans id="add-a-signature">Add a signature</Trans>,
        destination: SettingsHomeDestinationsEnum.ADD_SIGNATURE,
      },
    };
  }

  if (signaturesCount === 1) {
    return {
      icon: 'feather',
      title: <Trans id="manage-signatures">Manage signatures</Trans>,
      description: (
        <Trans id="create-custom-signatures-for-replying-from-different-inboxes">
          Create custom signatures for replying from different inboxes.
        </Trans>
      ),
      primary: {
        label: <Trans id="edit-signature">Edit signature</Trans>,
        destination: SettingsHomeDestinationsEnum.EDIT_SIGNATURE,
      },
      secondary: {
        label: <Trans id="add-signature">Add signature</Trans>,
        destination: SettingsHomeDestinationsEnum.ADD_SIGNATURE,
      },
    };
  }

  return {
    icon: 'feather',
    title: <Trans id="manage-signatures">Manage signatures</Trans>,
    description: (
      <Trans id="create-custom-signatures-for-replying-from-different-inboxes">
        Create custom signatures for replying from different inboxes.
      </Trans>
    ),
    primary: {
      label: <Trans id="add-a-signature">Add a signature</Trans>,
      destination: SettingsHomeDestinationsEnum.ADD_SIGNATURE,
    },
    secondary: {
      label: <Trans id="review-all">Review all</Trans>,
      destination: SettingsHomeDestinationsEnum.REVIEW_SIGNATURES,
    },
  };
}

// TODO: move into its own file when settings home is re-implemented in NF
export enum NamespaceSelectResourceTypesEnum {
  ACTIVITY_EXPORT = 'activityExport',
  ANSWERS = 'answers',
  COMPANY_PREFERENCE = 'COMPANY_PREFERENCE',
  CUSTOM_VIEWS = 'views',
  IMPORT_ROUTE = 'IMPORT_ROUTE',
  INBOXES = 'inboxes',
  INTEGRATION = 'INTEGRATION',
  LOAD_BALANCING = 'loadBalancing',
  PERSONAL_PREFERENCE = 'PERSONAL_PREFERENCE',
  PLUGIN = 'PLUGIN',
  ROLES = 'roles',
  RULES = 'rules',
  SHIFTS = 'shifts',
  SIGNATURES = 'signatures',
  TAGS = 'tags',
  TEAMMATE = 'teammate',
  USER_PROVISIONING = 'userProvisioning',
  VACATION_RESPONDER = 'VACATION_RESPONDER',
}
export const isNamespaceSelectResourceType = makeEnumRuntimeType(NamespaceSelectResourceTypesEnum).is;

export const makePathFromNamespaceSelectResourceType = (
  namespace: string,
  isNew: boolean,
  resource: NamespaceSelectResourceTypesEnum,
): SettingsPathId => {
  if (resource === NamespaceSelectResourceTypesEnum.USER_PROVISIONING) {
    return 'settings.teammates.provisioning';
  }

  if (resource === NamespaceSelectResourceTypesEnum.ACTIVITY_EXPORT) {
    return 'settings.teammates.export';
  }

  if (resource === NamespaceSelectResourceTypesEnum.RULES) {
    if (isNew) {
      return namespace && isTeamNamespace(namespace) ? 'settings.rules.ui.library' : 'settings.rules.ui.new';
    }

    return 'settings.rules.ui';
  }
  if (resource === NamespaceSelectResourceTypesEnum.INBOXES) {
    if (isNew) {
      return 'settings.inboxes.new';
    }

    return 'settings.inboxes';
  }
  if (resource === NamespaceSelectResourceTypesEnum.SIGNATURES) {
    if (isNew) {
      return 'settings.signatures.new';
    }

    return 'settings.signatures';
  }
  if (resource === NamespaceSelectResourceTypesEnum.ANSWERS) {
    if (isNew) {
      return 'settings.answers.new';
    }

    return 'settings.answers';
  }
  if (resource === NamespaceSelectResourceTypesEnum.CUSTOM_VIEWS) {
    if (isNew) {
      return 'settings.custom-views.new';
    }

    return 'settings.custom-views';
  }
  if (resource === NamespaceSelectResourceTypesEnum.TAGS) {
    if (isNew) {
      return 'settings.tags.new';
    }

    return 'settings.tags';
  }
  if (resource === NamespaceSelectResourceTypesEnum.ROLES) {
    if (isNew) {
      return 'settings.roles.new';
    }

    return 'settings.roles';
  }
  if (resource === NamespaceSelectResourceTypesEnum.SHIFTS) {
    return 'settings.shifts';
  }

  if (resource === NamespaceSelectResourceTypesEnum.LOAD_BALANCING) {
    return 'settings.rules.ui.limits.list';
  }

  if (isNew) {
    return 'settings.teammates.new';
  }

  return 'settings.teammates.list';
};
